// Quote Builder configuration: questions, packages, recommendation logic

export const QUESTIONS = [
  {
    id: "business_type",
    title: "What best describes your business?",
    subtitle: "Pick the closest match — it helps us tailor the recommendation.",
    type: "single",
    options: [
      { value: "Tradesperson", icon: "Hammer", desc: "Electrician, plumber, builder, gardener" },
      { value: "Restaurant / Café", icon: "UtensilsCrossed", desc: "Food, drink & hospitality" },
      { value: "Professional Service", icon: "Briefcase", desc: "Accountant, consultant, lawyer" },
      { value: "Retail", icon: "ShoppingBag", desc: "Physical store or online products" },
      { value: "Healthcare", icon: "Stethoscope", desc: "Clinic, therapist, dentist" },
      { value: "Fitness", icon: "Dumbbell", desc: "Gym, studio, coach, personal trainer" },
      { value: "Estate Agent", icon: "Home", desc: "Sales, lettings, property services" },
      { value: "Startup", icon: "Rocket", desc: "Early-stage tech or product" },
      { value: "Charity", icon: "HeartHandshake", desc: "Not-for-profit or community" },
      { value: "Other", icon: "Sparkles", desc: "Something else entirely" },
    ],
  },
  {
    id: "has_website",
    title: "Do you already have a website?",
    subtitle: "This tells us whether we're starting fresh or rebuilding.",
    type: "single",
    options: [
      { value: "No", icon: "PlusCircle", desc: "Starting from scratch" },
      { value: "Yes, but it needs redesigning", icon: "Repeat", desc: "Time for a refresh" },
      { value: "Yes, and I'm happy with it", icon: "CheckCircle2", desc: "Just exploring new features" },
    ],
  },
  {
    id: "main_goal",
    title: "What are you mainly looking for?",
    subtitle: "Pick the service that best matches what you need.",
    type: "single",
    options: [
      { value: "Website Design", icon: "LayoutTemplate", desc: "A brand new site, built from scratch" },
      { value: "Website Redesign", icon: "RefreshCw", desc: "Refresh or rebuild an existing site" },
      { value: "Website Care Plans", icon: "ShieldCheck", desc: "Ongoing maintenance & support" },
    ],
  },
  {
    id: "page_count",
    title: "Approximately how many pages do you need?",
    subtitle: "A rough guess is fine — we'll refine it together.",
    type: "single",
    options: [
      { value: "1", icon: "FileText", desc: "Single focused landing page" },
      { value: "2-3", icon: "Files", desc: "A few core pages" },
      { value: "4-5", icon: "Layers", desc: "The essentials, well done" },
      { value: "6-8", icon: "LayoutGrid", desc: "Room to grow" },
      { value: "9+", icon: "Layers3", desc: "A proper multi-page site" },
    ],
  },
  {
    id: "timeline",
    title: "How quickly do you need your project?",
    subtitle: "We'll try to match your pace — but honesty helps.",
    type: "single",
    options: [
      { value: "Within a week", icon: "Zap", desc: "Rush timeline" },
      { value: "2–4 weeks", icon: "Timer", desc: "Standard pace" },
      { value: "Within 2 months", icon: "Calendar", desc: "Room to breathe" },
      { value: "Just exploring", icon: "Compass", desc: "No rush" },
    ],
  },
  {
    id: "budget", title: "What's your approximate budget?", subtitle: "This helps us recommend the right fit — no judgement.", type: "single", options: [
      { value: "under_500", icon: "CircleDollarSign", label: { GBP: "Under £500", CAD: "Under CA$700" } },
      { value: "between_500_750", icon: "CircleDollarSign", label: { GBP: "£500–£750", CAD: "CA$700–$1,000" } },
      { value: "between_750_1000", icon: "CircleDollarSign", label: { GBP: "£750–£1,000", CAD: "CA$1,000–$1,500" } },
      { value: "over_1000", icon: "CircleDollarSign", label: { GBP: "£1,000+", CAD: "CA$1,500+" } },
      { value: "not_sure", icon: "HelpCircle", label: { GBP: "Not sure", CAD: "Not sure" } },
    ],
  },
];

// The two services that go through the full page-count/timeline/budget quiz
// and get a package recommendation. Everything else in main_goal's options
// is treated as a lightweight service request (see Pricing.jsx branching).
export const BUILD_GOALS = ["Website Design", "Website Redesign"];

// One-off website build packages.
export const PACKAGES = {
  launch: {
    key: "launch",
    name: "Launch",

    prices: {
      GBP: 299,
      CAD: 559,
    },

    timeline: "1–2 weeks to launch",
    bestFor: "A focused, fast site for businesses just getting started.",

    included: [
      "3 pages included",
      "Mobile optimisation",
      "Website deployment",
      "1 contact form",
      "Google Maps integration",
      "Basic SEO setup",
      "1 round of revisions",
    ],

    optional: ["Care plan add-on"],
  },

  growth: {
    key: "growth",
    name: "Growth",

    prices: {
      GBP: 399,
      CAD: 749,
    },

    timeline: "2–3 weeks to launch",
    bestFor: "Businesses that need more room to tell their story.",

    included: [
      "5 pages included",
      "Mobile optimisation",
      "Website deployment",
      "2 contact forms",
      "Google Maps integration",
      "Standard SEO setup",
      "2 rounds of revisions",
    ],

    optional: ["Care plan add-on"],
  },

  expand: {
    key: "expand",
    name: "Expand",

    prices: {
      GBP: 599,
      CAD: 1129,
    },

    timeline: "3–4 weeks to launch",
    bestFor: "A proper multi-page presence with stronger SEO foundations.",

    included: [
      "8 pages included",
      "Mobile optimisation",
      "Website deployment",
      "2 contact forms",
      "Google Maps integration",
      "Advanced SEO setup",
      "2 rounds of revisions",
    ],

    optional: ["Care plan add-on"],
  },
};


// Ongoing care plans.
export const CARE_PLANS = {
  essential: {
    key: "essential",
    name: "Essential Care",
    prices: {
      GBP: 29,
      CAD: 54,
    },
  },

  business: {
    key: "business",
    name: "Business Care",
    prices: {
      GBP: 49,
      CAD: 92,
    },
  },

  premium: {
    key: "premium",
    name: "Premium Care",
    prices: {
      GBP: 89,
      CAD: 169,
    },
  },
};


const BUDGET_LIMITS = {
  GBP: {
    under_500: 500,
    between_500_750: 750,
    between_750_1000: 1000,
    over_1000: Infinity,
    not_sure: Infinity,
  },

  CAD: {
    under_500: 700,
    between_500_750: 1000,
    between_750_1000: 1500,
    over_1000: Infinity,
    not_sure: Infinity,
  },
};

export function recommendPackage(answers, currency = "GBP") {
  const { page_count, budget } = answers;

  let key = "expand";

  if (page_count === "1" || page_count === "2-3") {
    key = "launch";
  } else if (page_count === "4-5") {
    key = "growth";
  } else {
    key = "expand";
  }

  const idealKey = key;

  const tierPrice = PACKAGES[key].prices[currency];

  const budgetCeiling =
    BUDGET_LIMITS[currency]?.[budget] ?? Infinity;

  if (tierPrice > budgetCeiling) {
    key = budgetCeiling >= PACKAGES.growth.prices[currency]
      ? "growth"
      : "launch";
  }

  const wasDowngraded = key !== idealKey;
  const pagesIncluded = PACKAGES[key].included[0];

  let reason;


  if (wasDowngraded) {
    reason =
      `To keep things within your budget, we've suggested ${PACKAGES[key].name} rather than a larger package. ` +
      `It covers ${pagesIncluded.replace(" included", "")} — if you need more than that, we can either phase the extra pages in later or talk through a custom scope that fits your budget differently.`;
  } else if (key === "launch") {
    reason =
      "You're after a focused, fast site — Launch gets three sharp pages live in 1–2 weeks, and it's eligible for a care plan whenever you want ongoing support.";
  } else if (key === "growth") {
    reason =
      "With more to say, Growth gives you room to tell a fuller story — five pages, two contact forms and stronger SEO foundations.";
  } else {
    reason =
      page_count === "9+"
        ? "You need a proper multi-page presence. Expand covers up to eight pages — for 9 or more we'll scope a custom build together, but this gives you the right starting point."
        : "You need a proper multi-page presence — Expand covers up to eight pages with advanced SEO and two rounds of revisions.";
  }

  const alternatives = Object.keys(PACKAGES)
    .filter((k) => k !== key)
    .slice(0, 2);

  return {
    key,
    reason,
    alternatives,
    wasDowngraded,
  };
}